/**
 * AdminDownloadsView — Centre de téléchargements pour l'administrateur.
 * Permet de télécharger : listes bénéficiaires, feuilles d'émargement,
 * synthèses collectives, rapports intervenants, et d'accéder aux CV uploadés.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Download, FileText, Users, CalendarDays, BarChart3,
  FileDown, ClipboardList, RefreshCw, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
});

const downloadFile = async (url, filename, setLoading) => {
  if (setLoading) setLoading(true);
  try {
    const r = await fetch(url, { headers: authHeaders() });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || `Erreur HTTP ${r.status}`);
    }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Téléchargement de « ${filename} » lancé`);
  } catch (e) {
    toast.error(e.message || 'Erreur lors du téléchargement');
  } finally {
    if (setLoading) setLoading(false);
  }
};

// ── Section : Listes et exports ─────────────────────────────────────────────

const ListesSection = ({ beneficiaires, zones }) => {
  const [zone, setZone] = useState('all');
  const [statut, setStatut] = useState('all');
  const [loading, setLoading] = useState(false);

  const handleDownload = () => {
    const params = new URLSearchParams();
    if (zone !== 'all') params.append('zone', zone);
    if (statut !== 'all') params.append('statut', statut);
    const q = params.toString() ? `?${params}` : '';
    const suffix = [zone !== 'all' ? zone : '', statut !== 'all' ? statut : ''].filter(Boolean).join('_');
    const filename = `liste_beneficiaires${suffix ? '_' + suffix : ''}_${new Date().toISOString().slice(0, 10)}.pdf`;
    downloadFile(`${API_BASE}/reporting/liste-beneficiaires/pdf${q}`, filename, setLoading);
  };

  return (
    <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold flex items-center gap-2 text-slate-800">
          <Users className="w-5 h-5 text-blue-600" />
          Liste des bénéficiaires
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Zone géographique</Label>
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger className="rounded-xl text-sm">
                <SelectValue placeholder="Toutes zones" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes zones</SelectItem>
                {zones.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Statut</Label>
            <Select value={statut} onValueChange={setStatut}>
              <SelectTrigger className="rounded-xl text-sm">
                <SelectValue placeholder="Tous statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="actif">Actif</SelectItem>
                <SelectItem value="en_attente_consentement">En attente consentement</SelectItem>
                <SelectItem value="refus_dispositif">Refus dispositif</SelectItem>
                <SelectItem value="sorti">Sorti</SelectItem>
                <SelectItem value="inactif">Inactif</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          onClick={handleDownload}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
        >
          {loading
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération…</>
            : <><Download className="w-4 h-4 mr-2" />Télécharger la liste PDF</>}
        </Button>
      </CardContent>
    </Card>
  );
};

// ── Section : Émargements ───────────────────────────────────────────────────

const EmargementSection = ({ ateliers }) => {
  const [selectedAtelier, setSelectedAtelier] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDownload = () => {
    if (!selectedAtelier) { toast.error('Sélectionnez un atelier'); return; }
    const atelier = ateliers.find(a => a.id === selectedAtelier);
    const filename = `emargement_${atelier?.titre?.replace(/\s+/g, '_') || selectedAtelier}_${atelier?.date || ''}.pdf`;
    downloadFile(`${API_BASE}/reporting/feuille-emargement/atelier/${selectedAtelier}`, filename, setLoading);
  };

  return (
    <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold flex items-center gap-2 text-slate-800">
          <ClipboardList className="w-5 h-5 text-emerald-600" />
          Feuilles d'émargement (ateliers)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-xs text-slate-600 mb-1 block">Atelier</Label>
          <Select value={selectedAtelier} onValueChange={setSelectedAtelier}>
            <SelectTrigger className="rounded-xl text-sm">
              <SelectValue placeholder="Sélectionner un atelier…" />
            </SelectTrigger>
            <SelectContent>
              {ateliers.sort((a, b) => b.date?.localeCompare(a.date || '') || 0).map(a => (
                <SelectItem key={a.id} value={a.id}>
                  {a.date} — {a.titre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={handleDownload}
          disabled={loading || !selectedAtelier}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
        >
          {loading
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération…</>
            : <><Download className="w-4 h-4 mr-2" />Télécharger la feuille PDF</>}
        </Button>
      </CardContent>
    </Card>
  );
};

// ── Section : Synthèses ─────────────────────────────────────────────────────

const SyntheseSection = () => {
  const [mois, setMois] = useState(new Date().getMonth() + 1);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);

  const moisLabels = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  const anneesDisponibles = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

  return (
    <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold flex items-center gap-2 text-slate-800">
          <BarChart3 className="w-5 h-5 text-purple-600" />
          Synthèse collective mensuelle
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Mois</Label>
            <Select value={String(mois)} onValueChange={v => setMois(Number(v))}>
              <SelectTrigger className="rounded-xl text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {moisLabels.map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Année</Label>
            <Select value={String(annee)} onValueChange={v => setAnnee(Number(v))}>
              <SelectTrigger className="rounded-xl text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {anneesDisponibles.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          onClick={() => downloadFile(
            `${API_BASE}/reporting/synthese-collective/${mois}/${annee}`,
            `synthese_collective_${moisLabels[mois - 1]}_${annee}.pdf`,
            setLoading
          )}
          disabled={loading}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
        >
          {loading
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération…</>
            : <><Download className="w-4 h-4 mr-2" />Télécharger la synthèse PDF</>}
        </Button>
      </CardContent>
    </Card>
  );
};

// ── Section : Rapports intervenants ────────────────────────────────────────

const RapportIntervenantSection = ({ users }) => {
  const today = new Date().toISOString().split('T')[0];
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  const [dateDebut, setDateDebut] = useState(firstOfMonth);
  const [dateFin, setDateFin] = useState(today);
  const [loading, setLoading] = useState(false);

  const handleDownload = () => {
    const url = `${API_BASE}/reporting/rapport-activite-intervenant?date_debut=${dateDebut}&date_fin=${dateFin}`;
    downloadFile(url, `rapport_intervenant_${dateDebut}_${dateFin}.pdf`, setLoading);
  };

  return (
    <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold flex items-center gap-2 text-slate-800">
          <FileText className="w-5 h-5 text-orange-500" />
          Rapport d'activité (mon compte)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Début</Label>
            <Input
              type="date"
              value={dateDebut}
              onChange={e => setDateDebut(e.target.value)}
              className="rounded-xl text-sm"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Fin</Label>
            <Input
              type="date"
              value={dateFin}
              onChange={e => setDateFin(e.target.value)}
              className="rounded-xl text-sm"
            />
          </div>
        </div>
        <Button
          onClick={handleDownload}
          disabled={loading}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
        >
          {loading
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération…</>
            : <><Download className="w-4 h-4 mr-2" />Télécharger le rapport PDF</>}
        </Button>
      </CardContent>
    </Card>
  );
};

// ── Composant principal ─────────────────────────────────────────────────────

const AdminDownloadsView = ({ beneficiaires = [], ateliers = [], users = [] }) => {
  const zones = [...new Set(beneficiaires.map(b => b.zone_geographique).filter(Boolean))].sort();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <FileDown className="w-6 h-6 text-blue-600" />
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
          Centre de téléchargements
        </h2>
        <Badge className="bg-blue-100 text-blue-700 text-xs">Admin</Badge>
      </div>

      <p className="text-sm text-slate-500">
        Téléchargez tous les documents PDF générés par la plateforme : listes, émargements, synthèses et rapports.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ListesSection beneficiaires={beneficiaires} zones={zones} />
        <EmargementSection ateliers={ateliers} />
        <SyntheseSection />
        <RapportIntervenantSection users={users} />
      </div>
    </div>
  );
};

export default AdminDownloadsView;
